import { useState, useEffect, useRef, useCallback } from 'react';
import { Typography, TextField, Button, Popover, Box } from '@mui/material';
import { useAuth } from '../Firebase/AuthProvider';
import { useFirestore } from '../Firebase/FirestoreProvider';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { fullCenter, pastelBlue } from '../Pages/Main/constants';

export function useDebounce(fn, delay) {
    const timer = useRef(null);

    return useCallback((...args) => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            fn(...args);
        }, delay);
    }, [fn, delay]);
}

export const getViewText = (editingText) => {
    try {
        return editingText
            .replace(/\*\*([\s\S]*?)\*\*/g, "<b>$1</b>")
            .replace(/\r?\n/g, "<br/>");
    } catch (e) {
        return editingText;
    }
};

export const getInputText = (viewText) => {
    try {
        return viewText
            .replace(/<b>([\s\S]*?)<\/b>/g, "**$1**")
            .replace(/<br\s*\/?>/g, "\n");
    } catch (e) {
        return viewText;
    }
};


export const EditableTypography = ({ fireKey, noPropagation = false, comment, path, autoSave = false,
    right = 0, bottom = -30, top = 'auto', left = 'auto',
    changeValue, modifyVisible, textAfter, ...props }) => {
    const { getData, changeData, saveData } = useFirestore();
    const { isAdmin } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const value = getData(fireKey, path);
    const disablePropagation = Boolean(isAdmin && noPropagation);

    const debouncedSave = useDebounce(() => {
        saveData();
    }, 1000);

    const handleClick = (e) => {
        if (disablePropagation) {
            e.preventDefault();
            e.stopPropagation();
        }
        setAnchorEl(e.currentTarget);
    };

    const handleClose = (e) => {
        if (disablePropagation) {
            e.preventDefault();
            e.stopPropagation();
        }
        setAnchorEl(null);
    };

    const handleChange = (e) => {
        if (disablePropagation) {
            e.preventDefault();
            e.stopPropagation();
        }
        let value = e.target.value;
        if (changeValue) {
            value = changeValue(value);
        }
        changeData(fireKey, value, path);
        if (autoSave) {
            debouncedSave();
        }
    }


    return <div style={{ position: 'relative', width: 'fit-content', height: 'fit-content' }}>
        <div style={{ position: 'relative' }}>
            <Typography style={{ zIndex: 5, position: 'relative', ...(Boolean(!value) && { display: 'none' }) }} {...props}>
                <span
                    dangerouslySetInnerHTML={{ __html: getViewText(modifyVisible ? modifyVisible(value) : value) }}
                />
                {Boolean(textAfter) && textAfter}

            </Typography>
            {isAdmin && <Box component='span'
                sx={{
                    width: '34px', height: '34px', maxWidth: '34px', maxHeight: '34px', minWidth: '34px', borderRadius: '50%',
                    bgcolor: pastelBlue,
                    scale: 1,
                    transition: 'scale 0.2s ease-in-out',
                    "&:hover": { scale: 1.05 },
                    zIndex: 14, position: 'absolute', right, bottom, top, left, cursor: 'pointer', ...fullCenter
                }}
                onClick={(e) => {
                    if (disablePropagation) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    handleClick(e);
                }} onMouseDown={(e) => {
                    if (disablePropagation) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}>

                <EditRoundedIcon sx={{ color: 'white' }} />
            </Box>}
        </div>
        {isAdmin &&
            <>
                <Popover
                    disableScrollLock
                    id={fireKey}
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <Box sx={{ minWidth: '300px', width: '600px', maxWidth: '800px', p: '10px' }} onClick={(e) => {
                        if (disablePropagation) {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}>
                        <Typography sx={{ pb: '10px', fontWeight: 'bold', pointerEvents: 'none' }}>id: {fireKey || path}</Typography>
                        {Boolean(comment) && <Typography sx={{ pb: '10px', pointerEvents: 'none' }}>{comment}</Typography>}
                        <TextField
                            sx={{ width: '100%' }}
                            multiline
                            value={getInputText(value)}
                            onChange={handleChange}
                            onClick={(e) => {
                                if (disablePropagation) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            }}
                        />
                    </Box>
                </Popover>
            </>}
    </div>;
};
